import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { useJsApiLoader } from "@react-google-maps/api";
import { country, CountryCode } from "../models/countryModels";
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID } from "../config/googleMapsConfig";
import { dt } from "../themes/redesignTokens";

const getGoogleMapsApiKey = (): string => {
  return (window as any).app?.config?.googleMapsApiKey || "";
}

const findComponent = (components: any[], type: string): any | undefined =>
  components.find((c: any) => c.types?.includes(type));

const getAddressDataFromPlace = (place: any): AddressData | undefined => {
  // New API: place.addressComponents with shortText/longText
  const components = place?.addressComponents;
  if (!components || components.length === 0) return undefined;

  const address: AddressData = {
    address: [
      findComponent(components, "route")?.shortText,
      findComponent(components, "street_number")?.shortText,
    ].filter(Boolean).join(" "),
    zipCode: findComponent(components, "postal_code")?.shortText?.replaceAll(" ", ""),
    city: findComponent(components, "locality")?.shortText ||
      findComponent(components, "sublocality")?.shortText ||
      findComponent(components, "neighborhood")?.shortText ||
      findComponent(components, "administrative_area_level_3")?.shortText,
    latitude: place.location?.lat(),
    longitude: place.location?.lng(),
  };

  const countryVal = findComponent(components, "country")?.shortText;
  if (countryVal) {
    address.countryCode = CountryCode[countryVal as any] as any || undefined;
  }

  return address;
}

export interface AddressData {
  address?: string;
  city?: string;
  countryCode?: CountryCode;
  zipCode?: string;
  latitude?: number;
  longitude?: number;
}

export interface AddressAutocompleteProps {
  countryCode?: CountryCode;
  placeholder?: string;
  defaultValue?: AddressData;
  onChange?: (value?: AddressData) => void;
}

/** Unique style ID so we only inject the global <style> once */
const STYLE_ID = "gmp-autocomplete-overrides";

/**
 * Injects a global <style> that targets the Shadow DOM internals of
 * <gmp-place-autocomplete> via ::part() selectors. This is the only
 * reliable way to override Google's default blue focus outline.
 */
const injectGlobalStyles = () => {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    /* ---- PlaceAutocompleteElement Shadow DOM overrides ---- */

    /* Remove Google's default focus ring on the host element */
    gmp-place-autocomplete {
      outline: none !important;
      box-shadow: none !important;
      border: none !important;
    }
    gmp-place-autocomplete:focus,
    gmp-place-autocomplete:focus-visible,
    gmp-place-autocomplete:focus-within {
      outline: none !important;
      box-shadow: none !important;
    }

    /* Target the internal input via ::part() */
    gmp-place-autocomplete::part(input) {
      font-family: 'Sora', var(--font-sans), sans-serif !important;
      font-size: 13px !important;
      font-weight: 400 !important;
      color: #1a1a2e !important;
      background: transparent !important;
      border: none !important;
      outline: none !important;
      box-shadow: none !important;
      padding: 0 !important;
      margin: 0 !important;
      width: 100% !important;
      height: auto !important;
      line-height: 1.5 !important;
    }
    gmp-place-autocomplete::part(input):focus,
    gmp-place-autocomplete::part(input):focus-visible {
      outline: none !important;
      box-shadow: none !important;
      border: none !important;
    }
    gmp-place-autocomplete::part(input)::placeholder {
      color: #9CA3AF !important;
      font-weight: 400 !important;
    }

    /* Kill any Google wrapper / container borders */
    gmp-place-autocomplete * {
      border: none !important;
      outline: none !important;
      box-shadow: none !important;
    }

    /* Predictions dropdown styling */
    gmp-place-autocomplete::part(predictions) {
      font-family: 'Sora', var(--font-sans), sans-serif !important;
      border-radius: 8px !important;
      border: 1px solid #E5E7EB !important;
      box-shadow: 0 4px 16px rgba(0,0,0,0.08) !important;
      margin-top: 4px !important;
    }
  `;
  document.head.appendChild(style);
};

const AddressAutocomplete = ({ countryCode, placeholder, defaultValue, onChange }: AddressAutocompleteProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const elementRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const apiKey = getGoogleMapsApiKey();
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const countryRestriction = countryCode && country(countryCode)?.alpha2;

  // Inject global ::part() styles once
  useEffect(() => { injectGlobalStyles(); }, []);

  useEffect(() => {
    if (!isLoaded || !containerRef.current || elementRef.current) return;

    const places = (google?.maps?.places as any);
    if (!places?.PlaceAutocompleteElement) return;

    const options: any = {};

    if (countryRestriction) {
      options.includedRegionCodes = [countryRestriction.toLowerCase()];
    }

    if (placeholder) {
      options.placeholder = placeholder;
    }

    const autocomplete = new places.PlaceAutocompleteElement(options);

    // Host-level styles — make the element fit inside our styled wrapper
    autocomplete.style.cssText = `
      display: block;
      width: 100%;
      border: none;
      outline: none;
      box-shadow: none;
      background: transparent;
      padding: 0;
      margin: 0;
    `;

    autocomplete.addEventListener("gmp-select", async (event: any) => {
      try {
        const placePrediction = event.placePrediction || event.detail?.placePrediction;
        if (!placePrediction) return;

        const place = placePrediction.toPlace();
        await place.fetchFields({
          fields: ["addressComponents", "location", "formattedAddress"],
        });

        const addressData = getAddressDataFromPlace(place);
        onChangeRef.current?.(addressData);
      } catch (err) {
        console.error("PlaceAutocompleteElement: error fetching place", err);
      }
    });

    containerRef.current.appendChild(autocomplete);
    elementRef.current = autocomplete;

    return () => {
      if (elementRef.current && containerRef.current) {
        try {
          containerRef.current.removeChild(elementRef.current);
        } catch { /* already removed */ }
        elementRef.current = null;
      }
    };
  }, [isLoaded, countryRestriction, placeholder]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        padding: "10px 14px",
        background: dt.cardBg,
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: dt.radiusSm,
        transition: "all 0.2s",
        cursor: "text",
        // Focus-within: the same glow as the GPS button hover
        "&:focus-within": {
          borderColor: dt.blue,
          boxShadow: `0 0 0 2px ${dt.blueGlow}`,
        },
        "&:hover": {
          borderColor: dt.blue,
        },
        // Ensure the web component fills the wrapper
        "& gmp-place-autocomplete": {
          display: "block",
          width: "100%",
          border: "none !important",
          outline: "none !important",
          boxShadow: "none !important",
          background: "transparent !important",
          padding: "0 !important",
          margin: "0 !important",
        },
      }}
    />
  );
};

export default AddressAutocomplete;
